import React, { ReactNode } from "react";
import { intl } from "LocaleWrapper";
import { useLazyQuery } from "@apollo/client";
import { Session } from "ezStore";

import { Box, CircularProgress, Popover } from "@mui/material";
import RouteIcon from "@mui/icons-material/Route";
import QueryBuilderIcon from "@mui/icons-material/QueryBuilder";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";

import useLayout from "components/layout/useLayout";
import usePopover from "components/hooks/usePopover";

import { calculateCallStatus } from "view/calls/utils/CallUtils";
import CallSideviewer from "view/calls/components/sideviewer/CallSideviewer";

import { FormattedDateWindow } from "utils/DateUtils";

import { ChildPropsFromArray } from "types/commons";
import { CallType, ContainerShipPlanningQuery } from "__generated__/graphql";
import { GetCallDocument } from "__generated__/view/calls/graphql/call.queries.generated";

type ShipPlanningItem = ChildPropsFromArray<ContainerShipPlanningQuery, "shipPlanning">;
type Voyage = ChildPropsFromArray<ShipPlanningItem, "voyages">;
type Call = ChildPropsFromArray<Voyage, "calls">;

const ContainerPlanningCallTask = ({
	call,
	voyage,
	onClick,
	fadeOut,
	isSelected,
	isPlanned,
	session,
	refetch,
}: {
	call: Call;
	voyage: Voyage;
	onClick: (c: Call) => void;
	fadeOut: boolean;
	isSelected: boolean;
	isPlanned: boolean;
	session: Session;
	refetch: () => void;
}) => {

	const layout = useLayout();

	const calculatedStatus = calculateCallStatus(call, session);
	const color = isPlanned ? call.lastStatusChange?.status.color : calculatedStatus.color;

	const { popoverProps, onPopoverOpen, onPopoverClose } = usePopover({
		trigger: "hover",
	});

	const [ getCalls ] = useLazyQuery(GetCallDocument);

	const handleOnCallRightClick = () => {
		layout.setDrawerSettings({
			open: true,
			title: intl.formatMessage({ id: "CALLS" }),
			content: (
				<Box p={2}>
					<CircularProgress size={24} />
				</Box>
			),
			size: "normal",
			variant: "overlay",
		});

		getCalls({
			variables: {
				id: call.id,
			},
		}).then(response => {
			layout.setDrawerSettings((prev) => ({
				...prev,
				content: (
					<CallSideviewer
						defaultCallId={response.data?.call?.voyage.calls?.[0]?.id ?? null}
						nextCallId={call.id}
						refetch={() => refetch()}
						bargeType={null}
						isShipEditable={false} />
				),
			}));
		});
	};

	return (
		<div
			className="call-task"
			onClick={() => onClick(call)}
			style={{ backgroundColor: isSelected ? "rgb(33, 150, 243)" : color, opacity: fadeOut ? 0.3 : 1 }}
			title="">
			<React.Fragment>
				<div className="title"
					onMouseEnter={onPopoverOpen}
					onMouseLeave={onPopoverClose}
					onContextMenu={handleOnCallRightClick}>
					{call.location.code || call.location.description}
				</div>

				<Popover {...popoverProps}>
					<PopoverContent call={call} voyage={voyage} />
				</Popover>
			</React.Fragment>
		</div>
	);
};

const PopoverContent = ({ call, voyage }: { call: Call; voyage: Voyage; }) => {

	const Row = ({ icon, children }: { icon: ReactNode; children: ReactNode }) => {
		return (
			<div className="row">
				<div className="icon">
					{icon}
				</div>

				<div>
					{children}
				</div>
			</div>
		);
	};

	const getExpectedContainerAmount = (callType: CallType, lengtType: "20ft" | "30ft" | "40ft" | "41ft", isReefer: boolean) => {
		return call.expectedCargos
			.filter(it => it.actionType === callType)
			.filter(it => {

				const lengthFt = it.containerType?.lengthFt ?? 0;

				const reeferCheck = (it?.containerType?.reefer ?? false) === isReefer;

				if (lengtType === "20ft" && reeferCheck) {
					return lengthFt <= 20;
				} else if (lengtType === "30ft" && reeferCheck) {
					return lengthFt > 20 && lengthFt <= 30;
				} else if (lengtType === "40ft" && reeferCheck) {
					return lengthFt > 30 && lengthFt <= 40;
				} else if (lengtType === "41ft" && reeferCheck) {
					return lengthFt > 40;
				} else {
					return false;
				}
			}).reduce((acc, current) => {
				return acc + current.amount;
			}, 0);
	};

	return (
		<div className="call-task-popover">
			<div className="info">
				{intl.formatMessage({ id: "RIGHT_CLICK_CALL_OPEN_SIDEVIEWER" })}
			</div>

			<Row icon={<RouteIcon />}>
				{voyage.voyageNumber}
			</Row>

			<Row icon={<QueryBuilderIcon />}>
				<FormattedDateWindow from={call.pta} to={call.ptd} />
			</Row>

			<Row icon={<DirectionsBoatIcon />}>
				{voyage.ship.name}
			</Row>

			<Row icon={<ZoomOutMapIcon />}>
				<div className="container-amount">
					<div>
						<span>{intl.formatMessage({ id: "EXPECTED" })} (loading)</span>

						<span>{getExpectedContainerAmount(CallType.Loading, "20ft", false)}/</span>
						<span>{getExpectedContainerAmount(CallType.Loading, "30ft", false)}/</span>
						<span>{getExpectedContainerAmount(CallType.Loading, "40ft", false)}/</span>
						<span style={{ marginRight: 16 }}>{getExpectedContainerAmount(CallType.Loading, "41ft", false)}</span>

						<span>{getExpectedContainerAmount(CallType.Loading, "20ft", true)}/</span>
						<span>{getExpectedContainerAmount(CallType.Loading, "30ft", true)}/</span>
						<span>{getExpectedContainerAmount(CallType.Loading, "40ft", true)}/</span>
						<span>{getExpectedContainerAmount(CallType.Loading, "41ft", true)}</span>
						<span>&nbsp;RF</span>
					</div>

					<div>
						<span>{intl.formatMessage({ id: "EXPECTED" })} (discharging)</span>

						<span>{getExpectedContainerAmount(CallType.Discharging, "20ft", false)}/</span>
						<span>{getExpectedContainerAmount(CallType.Discharging, "30ft", false)}/</span>
						<span>{getExpectedContainerAmount(CallType.Discharging, "40ft", false)}/</span>
						<span style={{ marginRight: 16 }}>{getExpectedContainerAmount(CallType.Discharging, "41ft", false)}</span>

						<span>{getExpectedContainerAmount(CallType.Discharging, "20ft", true)}/</span>
						<span>{getExpectedContainerAmount(CallType.Discharging, "30ft", true)}/</span>
						<span>{getExpectedContainerAmount(CallType.Discharging, "40ft", true)}/</span>
						<span>{getExpectedContainerAmount(CallType.Discharging, "41ft", true)}</span>
						<span>&nbsp;RF</span>
					</div>
				</div>
			</Row>

			<Row icon={<ZoomOutMapIcon />}>
				<div className="container-amount">
					<div>
						<span>{intl.formatMessage({ id: "CARGO_PLANNED" })} (loading)</span>

						<span>{call.numberOfLoadingContainers?.numLessThanOrEqualTo20ft ?? 0}/</span>
						<span>{call.numberOfLoadingContainers?.numFrom21ftThrough30ft ?? 0}/</span>
						<span>{call.numberOfLoadingContainers?.numFrom31ftThrough40ft ?? 0}/</span>
						<span style={{ marginRight: 16 }}>{call.numberOfLoadingContainers?.numGreaterThan40ft ?? 0}</span>

						<span>{call.numberOfLoadingReeferContainers?.numLessThanOrEqualTo20ft ?? 0}/</span>
						<span>{call.numberOfLoadingReeferContainers?.numFrom21ftThrough30ft ?? 0}/</span>
						<span>{call.numberOfLoadingReeferContainers?.numFrom31ftThrough40ft ?? 0}/</span>
						<span>{call.numberOfLoadingReeferContainers?.numGreaterThan40ft ?? 0}</span>
						<span>&nbsp;RF</span>
					</div>

					<div>
						<span>{intl.formatMessage({ id: "CARGO_PLANNED" })} (discharging)</span>

						<span>{call.numberOfDischargingContainers?.numLessThanOrEqualTo20ft ?? 0}/</span>
						<span>{call.numberOfDischargingContainers?.numFrom21ftThrough30ft ?? 0}/</span>
						<span>{call.numberOfDischargingContainers?.numFrom31ftThrough40ft ?? 0}/</span>
						<span style={{ marginRight: 16 }}>{call.numberOfDischargingContainers?.numGreaterThan40ft ?? 0}</span>

						<span>{call.numberOfDischargingContainers?.numLessThanOrEqualTo20ft ?? 0}/</span>
						<span>{call.numberOfDischargingContainers?.numFrom21ftThrough30ft ?? 0}/</span>
						<span>{call.numberOfDischargingContainers?.numFrom31ftThrough40ft ?? 0}/</span>
						<span>{call.numberOfDischargingContainers?.numGreaterThan40ft ?? 0}</span>
						<span>&nbsp;RF</span>
					</div>
				</div>
			</Row>
		</div>
	);
};

export default ContainerPlanningCallTask;
